import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    FaBookOpen, FaChalkboardTeacher, FaSearch, FaStar, FaStore, FaTags,
    FaUserTie, FaFilter, FaFire, FaMapMarkerAlt,
} from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import Cargando from '../../utils/Cargando';

const iconosCategoria = {
    cursos: FaBookOpen,
    asesorias: FaChalkboardTeacher,
};

const formatoPrecio = (valor) => `$${Number(valor || 0).toLocaleString('es-CO')}`;

export const Estrellas = ({ rating = 0, count = null, size = 'w-3.5 h-3.5' }) => (
    <span className="inline-flex items-center gap-1">
        <span className="inline-flex">
            {[1, 2, 3, 4, 5].map((n) => (
                <FaStar
                    key={n}
                    className={`${size} ${n <= Math.round(rating) ? 'text-amber-400' : 'text-slate-200'}`}
                />
            ))}
        </span>
        {rating > 0 && <span className="text-xs font-semibold text-slate-600">{Number(rating).toFixed(1)}</span>}
        {count !== null && count > 0 && <span className="text-xs text-slate-400">({count})</span>}
    </span>
);

export const ProductCard = ({ producto }) => (
    <article className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-[#1f94c2]/40 transition-all duration-300 flex flex-col">
        <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
            {producto.imagenPrincipal ? (
                <img
                    src={producto.imagenPrincipal}
                    alt={producto.titulo}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-4">
                    <FaBookOpen className="w-10 h-10 mb-2" />
                    <span className="text-xs">Sin imagen</span>
                </div>
            )}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                {producto.categoria && (
                    <span className="bg-[#205087]/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {producto.categoria.label}
                    </span>
                )}
                {producto.destacado && (
                    <span className="inline-flex items-center gap-1 bg-amber-500/95 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        <FaFire className="w-3 h-3" /> Destacado
                    </span>
                )}
            </div>
            {producto.descuentoPorcentaje > 0 && (
                <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    -{producto.descuentoPorcentaje}%
                </span>
            )}
        </div>
        <div className="p-4 flex flex-col flex-1">
            <h3 className="font-bold text-slate-800 mb-1 line-clamp-2">{producto.titulo}</h3>
            {(producto.ratingCount > 0) && (
                <div className="mb-1">
                    <Estrellas rating={producto.ratingPromedio} count={producto.ratingCount} />
                </div>
            )}
            {producto.resumen && (
                <p className="text-sm text-slate-500 mb-3 line-clamp-2">{producto.resumen}</p>
            )}
            {producto.vendedor && (
                <div className="flex items-center gap-2 mb-3">
                    {producto.vendedor.foto ? (
                        <img
                            src={producto.vendedor.foto}
                            alt={producto.vendedor.fullName}
                            className="w-7 h-7 rounded-full object-cover border border-slate-200"
                        />
                    ) : (
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">
                            {String(producto.vendedor.fullName || '?').charAt(0)}
                        </div>
                    )}
                    <span className="text-xs text-slate-600 font-medium truncate">
                        {producto.vendedor.fullName}
                    </span>
                    {producto.ciudad && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 ml-auto shrink-0">
                            <FaMapMarkerAlt className="w-3 h-3" /> {producto.ciudad}
                        </span>
                    )}
                </div>
            )}
            <div className="mt-auto flex items-center justify-between gap-2">
                <div className="flex flex-col">
                    {producto.descuentoPorcentaje > 0 && (
                        <span className="text-xs text-slate-400 line-through">
                            {formatoPrecio(producto.precio)}
                        </span>
                    )}
                    <span className="text-lg font-bold text-[#205087]">
                        {formatoPrecio(producto.precioFinal ?? producto.precio)} COP
                    </span>
                </div>
                <Link
                    to={`producto/${producto._id}`}
                    className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#f79865] hover:bg-[#e88a55] transition-colors shadow-sm"
                >
                    Comprar
                </Link>
            </div>
        </div>
    </article>
);

/**
 * REDPLIKAPLACE: marketplace de conocimiento de la red.
 * Catálogo público con búsqueda, filtros (categoría, profesión, modalidad, ciudad),
 * productos destacados y recomendados.
 */
const RedplikaPlace = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const categoriaActiva = searchParams.get('categoria') || '';

    const [categorias, setCategorias] = useState([]);
    const [profesiones, setProfesiones] = useState([]);
    const [recomendados, setRecomendados] = useState([]);
    const [productos, setProductos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [cargandoProductos, setCargandoProductos] = useState(false);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [busquedaAplicada, setBusquedaAplicada] = useState('');
    const [profesion, setProfesion] = useState('');
    const [modalidad, setModalidad] = useState('');
    const [ciudad, setCiudad] = useState('');
    const [ciudadAplicada, setCiudadAplicada] = useState('');
    const [orden, setOrden] = useState('recientes');

    useEffect(() => {
        Promise.all([
            apiClient.get('/api/marketplace/categorias').then(res => setCategorias(Array.isArray(res.data) ? res.data : [])).catch(() => setCategorias([])),
            apiClient.get('/api/professions').then(res => setProfesiones(res.data?.flat || [])).catch(() => setProfesiones([])),
            apiClient.get('/api/marketplace/publicaciones/recomendadas').then(res => setRecomendados(Array.isArray(res.data) ? res.data : [])).catch(() => setRecomendados([])),
        ]).finally(() => setCargando(false));
    }, []);

    useEffect(() => {
        setCargandoProductos(true);
        const params = new URLSearchParams();
        if (categoriaActiva) params.set('categoria', categoriaActiva);
        if (busquedaAplicada) params.set('q', busquedaAplicada);
        if (profesion) params.set('profesion', profesion);
        if (modalidad) params.set('modalidad', modalidad);
        if (ciudadAplicada) params.set('ciudad', ciudadAplicada);
        if (orden) params.set('orden', orden);

        apiClient.get(`/api/marketplace/publicaciones?${params.toString()}`)
            .then(res => setProductos(Array.isArray(res.data) ? res.data : []))
            .catch(() => setProductos([]))
            .finally(() => setCargandoProductos(false));
    }, [categoriaActiva, busquedaAplicada, profesion, modalidad, ciudadAplicada, orden]);

    const categoriaLabel = useMemo(
        () => categorias.find(c => c.value === categoriaActiva)?.label || '',
        [categorias, categoriaActiva]
    );

    const hayFiltrosActivos = busquedaAplicada || profesion || modalidad || ciudadAplicada || categoriaActiva;

    const seleccionarCategoria = (value) => {
        if (!value) setSearchParams({});
        else setSearchParams({ categoria: value });
    };

    const aplicarBusqueda = (e) => {
        e.preventDefault();
        setBusquedaAplicada(busqueda.trim());
        setCiudadAplicada(ciudad.trim());
    };

    const limpiarFiltros = () => {
        setBusqueda('');
        setBusquedaAplicada('');
        setProfesion('');
        setModalidad('');
        setCiudad('');
        setCiudadAplicada('');
        setOrden('recientes');
        setSearchParams({});
    };

    if (cargando) return <Cargando message="Cargando REDPLIKAPLACE..." />;

    return (
        <div className="min-h-[70vh] bg-gradient-to-b from-slate-50 to-white">
            {/* Encabezado */}
            <div className="bg-gradient-to-r from-[#205087] to-[#1f94c2] text-white">
                <div className="w-full px-4 sm:px-6 py-10 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
                        <FaStore className="w-4 h-4" />
                        REDPLIKAPLACE
                    </div>
                    <h1 className="text-2xl sm:text-4xl font-bold mb-3 max-w-4xl mx-auto leading-tight">
                        Tu experiencia también puede convertirse en oportunidades.
                    </h1>
                    <p className="text-white/85 max-w-3xl mx-auto text-sm sm:text-base">
                        Un espacio para que los profesionales de REDPLIKA creen, publiquen y ofrezcan cursos, asesorías y servicios desde un solo lugar.
                    </p>
                    <p className="mt-3 mb-6 text-sm sm:text-base font-semibold tracking-wide">
                        Crea. Comparte. Conecta.
                    </p>
                    {/* Buscador */}
                    <form onSubmit={aplicarBusqueda} className="max-w-xl mx-auto flex">
                        <input
                            type="text"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Busca cursos, asesorías o servicios..."
                            className="flex-1 rounded-l-xl px-4 py-3 text-sm text-slate-700 focus:outline-none"
                        />
                        <button
                            type="submit"
                            className="rounded-r-xl px-5 bg-[#f79865] hover:bg-[#e88a55] transition-colors"
                            aria-label="Buscar"
                        >
                            <FaSearch className="w-4 h-4 text-white" />
                        </button>
                    </form>
                </div>
            </div>

            <div className="w-full px-4 sm:px-6 py-8">
                {/* Recomendados (solo sin filtros) */}
                {!hayFiltrosActivos && recomendados.length > 0 && (
                    <div className="mb-10">
                        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <FaFire className="text-[#f79865]" /> Recomendados y más vendidos
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {recomendados.map((p) => <ProductCard key={`rec-${p._id}`} producto={p} />)}
                        </div>
                    </div>
                )}

                {/* Categorías */}
                <div className="mb-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <FaTags className="text-[#1f94c2]" /> Categorías
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => seleccionarCategoria('')}
                            className={`px-5 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${!categoriaActiva
                                ? 'bg-[#205087] text-white border-[#205087] shadow-md'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-[#1f94c2] hover:text-[#1f94c2]'}`}
                        >
                            Todos
                        </button>
                        {categorias.map((cat) => {
                            const Icono = iconosCategoria[cat.value] || FaUserTie;
                            const activa = categoriaActiva === cat.value;
                            return (
                                <button
                                    key={cat.value}
                                    type="button"
                                    onClick={() => seleccionarCategoria(cat.value)}
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${activa
                                        ? 'bg-[#205087] text-white border-[#205087] shadow-md'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-[#1f94c2] hover:text-[#1f94c2]'}`}
                                >
                                    <Icono className="w-4 h-4" />
                                    {cat.label}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setMostrarFiltros(v => !v)}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ml-auto ${mostrarFiltros
                                ? 'bg-[#1f94c2] text-white border-[#1f94c2]'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-[#1f94c2] hover:text-[#1f94c2]'}`}
                        >
                            <FaFilter className="w-4 h-4" /> Filtros
                        </button>
                    </div>
                </div>

                {/* Filtros avanzados */}
                {mostrarFiltros && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Profesión</label>
                                <select
                                    value={profesion}
                                    onChange={(e) => setProfesion(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                >
                                    <option value="">Todas</option>
                                    {profesiones.map((p) => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Modalidad</label>
                                <select
                                    value={modalidad}
                                    onChange={(e) => setModalidad(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                >
                                    <option value="">Todas</option>
                                    <option value="Virtual">Virtual</option>
                                    <option value="Presencial">Presencial</option>
                                    <option value="Mixta">Mixta</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Ciudad</label>
                                <input
                                    type="text"
                                    value={ciudad}
                                    onChange={(e) => setCiudad(e.target.value)}
                                    onBlur={() => setCiudadAplicada(ciudad.trim())}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setCiudadAplicada(ciudad.trim()); } }}
                                    placeholder="Ej: Bogotá"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Ordenar por</label>
                                <select
                                    value={orden}
                                    onChange={(e) => setOrden(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                >
                                    <option value="recientes">Más recientes</option>
                                    <option value="ventas">Más vendidos</option>
                                    <option value="rating">Mejor valorados</option>
                                    <option value="precio_asc">Menor precio</option>
                                    <option value="precio_desc">Mayor precio</option>
                                </select>
                            </div>
                        </div>
                        {hayFiltrosActivos && (
                            <button
                                type="button"
                                onClick={limpiarFiltros}
                                className="mt-3 text-sm font-semibold text-[#1f94c2] hover:underline"
                            >
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                )}

                {/* Productos */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-800">
                        {busquedaAplicada
                            ? `Resultados para: "${busquedaAplicada}"`
                            : categoriaActiva
                                ? `Mostrando: ${categoriaLabel}`
                                : 'Todos los productos'}
                    </h2>
                    <span className="text-sm text-slate-500">
                        {productos.length} publicación{productos.length === 1 ? '' : 'es'}
                    </span>
                </div>

                {cargandoProductos ? (
                    <div className="text-center py-16 text-slate-500">Cargando publicaciones...</div>
                ) : productos.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 text-slate-400 mb-4">
                            <FaStore className="w-8 h-8" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-700 mb-2">
                            No encontramos publicaciones con esos criterios
                        </h3>
                        <p className="text-slate-500 text-sm max-w-sm mx-auto mb-4">
                            Prueba con otra búsqueda o limpia los filtros para ver todo el catálogo.
                        </p>
                        {hayFiltrosActivos && (
                            <button
                                type="button"
                                onClick={limpiarFiltros}
                                className="text-sm font-semibold text-[#1f94c2] hover:underline"
                            >
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {productos.map((producto) => <ProductCard key={producto._id} producto={producto} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RedplikaPlace;
